import api from './api';

// ============================================
// INTERFACES DE TIPOS
// ============================================

// Interface para período de relatório
export interface PeriodoRelatorio {
  dataInicio: string;
  dataFim: string;
  filtroData?: boolean;
}

// ============================================
// FILTROS AVANÇADOS - FASE 1
// ============================================

export interface FiltrosRelatorio {
  dataInicio?: string;
  dataFim?: string;
  departamentos?: string[]; // IDs dos departamentos
  status?: string[]; // Lista de status
  prioridades?: string[]; // Lista de prioridades
  usuarios?: string[]; // IDs dos usuários
}

// Interface para resumo de tarefas
export interface ResumoTarefas {
  totalTarefas: number;
  tarefasConcluidas: number;
  tarefasPendentes: number;
  tarefasAtrasadas: number;
  taxaConclusao: number;
  tempoMedioConclusao: string;
}

// Interface para item de status
export interface ItemStatus {
  status: string;
  quantidade: number;
}

// Interface para item de prioridade
export interface ItemPrioridade {
  prioridade: string;
  quantidade: number;
}

// Interface para performance de usuário
export interface PerformanceUsuario {
  usuarioId: string;
  nome: string;
  tarefasAtivas: number;
  tarefasConcluidas: number;
  tarefasAtrasadas: number;
  taxaConclusao: number;
}

// Interface para tendência diária
export interface TendenciaDiaria {
  data: string;
  criadas: number;
  concluidas: number;
}

// Interface para tendência pessoal
export interface TendenciaPessoal {
  data: string;
  recebidas: number;
  concluidas: number;
}

// Interface para departamento no relatório
export interface DepartamentoRelatorio {
  id: string;
  nome: string;
}

// ============================================
// RELATÓRIO DO LÍDER
// ============================================

export interface RelatorioLiderResponse {
  success: boolean;
  periodo: PeriodoRelatorio;
  departamento: DepartamentoRelatorio;
  resumo: ResumoTarefas;
  porStatus: ItemStatus[];
  porPrioridade: ItemPrioridade[];
  porUsuario: PerformanceUsuario[];
  tendencias: TendenciaDiaria[];
  // Novas métricas - FASE 2 (escopo do departamento)
  tempoMedioPorStatus?: TempoMedioStatus[];
  produtividadeDetalhada?: ProdutividadeUsuarioDetalhada[];
  tarefasMaisAntigas?: TarefaAntiga[];
  gargalosIdentificados?: Gargalo[];
}

export const obterRelatorioLider = async (
  dataInicio?: string,
  dataFim?: string,
  filtros?: FiltrosRelatorio
): Promise<RelatorioLiderResponse> => {
  try {
    const params: any = {};
    if (dataInicio) params.dataInicio = dataInicio;
    if (dataFim) params.dataFim = dataFim;
    
    // Adicionar filtros se fornecidos (líder não filtra departamento)
    if (filtros) {
      if (filtros.status && filtros.status.length > 0) {
        params.status = JSON.stringify(filtros.status);
      }
      if (filtros.prioridades && filtros.prioridades.length > 0) {
        params.prioridades = JSON.stringify(filtros.prioridades);
      }
      if (filtros.usuarios && filtros.usuarios.length > 0) {
        params.usuarios = JSON.stringify(filtros.usuarios);
      }
    }

    console.log('📤 Enviando filtros para backend (Líder):', params);
    const response = await api.get('/relatorios/lider', { params });
    return response.data;
  } catch (error: any) {
    console.error('Erro ao obter relatório do líder:', error);
    throw new Error(error.response?.data?.message || 'Erro ao carregar relatório do líder');
  }
};

// ============================================
// RELATÓRIO ADMINISTRATIVO
// ============================================

export interface ResumoGeral {
  totalUsuarios: number;
  totalDepartamentos: number;
  totalTarefas: number;
  tarefasConcluidas: number;
  tarefasPendentes: number;
  tarefasAtrasadas: number;
  tarefasConcluidasComAtraso: number;
  taxaConclusaoGeral: number;
}

export interface PerformanceDepartamento {
  departamentoId: string;
  nome: string;
  totalTarefas: number;
  tarefasConcluidas: number;
  tarefasAtrasadas: number;
  tarefasConcluidasComAtraso: number;
  taxaConclusao: number;
  usuariosAtivos: number;
}

export interface TopUsuario {
  usuarioId: string;
  nome: string;
  departamento: string;
  totalTarefas: number;
  tarefasConcluidas: number;
  taxaConclusao: number;
}

export interface TarefasAtrasadas {
  total: number;
  porDepartamento: Record<string, number>;
}

export interface TendenciaMensal {
  mes: string;
  criadas: number;
  concluidas: number;
}

// ============================================
// NOVAS MÉTRICAS - FASE 2
// ============================================

// Tempo médio por status
export interface TempoMedioStatus {
  status: string;
  tempoMedio: number; // em dias
  totalTarefas: number;
  maisRapida: number; // em dias
  maisLenta: number; // em dias
}

// Tempo médio por departamento
export interface TempoMedioDepartamento {
  departamentoId: string;
  nome: string;
  tempoMedio: number; // em dias
  totalConcluidas: number;
  maisRapida: number;
  maisLenta: number;
}

// Produtividade detalhada por usuário
export interface ProdutividadeUsuarioDetalhada {
  usuarioId: string;
  nome: string;
  departamento: string;
  tarefasAtivas: number;
  tarefasConcluidas: number;
  tarefasAtrasadas: number;
  tempoMedioConclusao: number; // em dias
  taxaConclusao: number;
  produtividadeScore: number; // 0-100
}

// Tarefas mais antigas (gargalos)
export interface TarefaAntiga {
  id: string;
  titulo: string;
  departamento: string;
  responsavel: string;
  status: string;
  prioridade: string;
  diasAberta: number;
  dataCriacao: string;
}

// Gargalos identificados
export interface Gargalo {
  tipo: 'status' | 'departamento' | 'usuario';
  identificador: string;
  nome: string;
  quantidade: number;
  tempoMedio: number;
  severidade: 'baixa' | 'media' | 'alta' | 'critica';
  descricao: string;
}

// ============================================
// MÉTRICAS DE COLABORADOR INDIVIDUAL
// ============================================

export interface MetricasColaboradorResponse {
  success: boolean;
  periodo: PeriodoRelatorio;
  colaborador: {
    id: string;
    nome: string;
    email: string;
    departamento: string;
  };
  metricas: {
    totalTarefas: number;
    tarefasConcluidas: number;
    tarefasConcluidasNoPrazo: number;
    tarefasConcluidasComAtraso: number;
    tarefasPendentes: number;
    tarefasAtrasadas: number;
    taxaConclusao: number;
    tempoMedioConclusao: number;
  };
}

export interface RelatorioAdminResponse {
  success: boolean;
  periodo: PeriodoRelatorio;
  resumoGeral: ResumoGeral;
  porDepartamento: PerformanceDepartamento[];
  topUsuarios: TopUsuario[];
  tarefasAtrasadas: TarefasAtrasadas;
  tendenciasMensais: TendenciaMensal[];
  // Novas métricas - FASE 2
  tempoMedioPorStatus?: TempoMedioStatus[];
  tempoMedioPorDepartamento?: TempoMedioDepartamento[];
  produtividadeDetalhada?: ProdutividadeUsuarioDetalhada[];
  tarefasMaisAntigas?: TarefaAntiga[];
  gargalosIdentificados?: Gargalo[];
}

export const obterRelatorioAdmin = async (
  dataInicio?: string,
  dataFim?: string,
  filtros?: FiltrosRelatorio
): Promise<RelatorioAdminResponse> => {
  try {
    const params: any = {};
    if (dataInicio) params.dataInicio = dataInicio;
    if (dataFim) params.dataFim = dataFim;
    
    // Adicionar filtros se fornecidos
    if (filtros) {
      if (filtros.departamentos && filtros.departamentos.length > 0) {
        params.departamentos = JSON.stringify(filtros.departamentos);
      }
      if (filtros.status && filtros.status.length > 0) {
        params.status = JSON.stringify(filtros.status);
      }
      if (filtros.prioridades && filtros.prioridades.length > 0) {
        params.prioridades = JSON.stringify(filtros.prioridades);
      }
      if (filtros.usuarios && filtros.usuarios.length > 0) {
        params.usuarios = JSON.stringify(filtros.usuarios);
      }
    }

    console.log('📤 [relatorioService] Params construídos:', params);
    console.log('📤 [relatorioService] Enviando para /relatorios/admin');
    const response = await api.get('/relatorios/admin', { params });
    console.log('📥 [relatorioService] Resposta recebida:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao obter relatório administrativo:', error);
    throw new Error(error.response?.data?.message || 'Erro ao carregar relatório administrativo');
  }
};

// ============================================
// RELATÓRIO PESSOAL
// ============================================

export interface RelatorioUsuarioResponse {
  success: boolean;
  periodo: PeriodoRelatorio;
  resumo: ResumoTarefas;
  porStatus: ItemStatus[];
  porPrioridade: ItemPrioridade[];
  tendencias: TendenciaPessoal[];
}

export const obterRelatorioUsuario = async (
  dataInicio?: string,
  dataFim?: string,
  filtros?: FiltrosRelatorio
): Promise<RelatorioUsuarioResponse> => {
  try {
    const params: any = {};
    if (dataInicio) params.dataInicio = dataInicio;
    if (dataFim) params.dataFim = dataFim;
    
    // Adicionar filtros se fornecidos (usuário não filtra departamento nem usuários)
    if (filtros) {
      if (filtros.status && filtros.status.length > 0) {
        params.status = JSON.stringify(filtros.status);
      }
      if (filtros.prioridades && filtros.prioridades.length > 0) {
        params.prioridades = JSON.stringify(filtros.prioridades);
      }
    }

    console.log('📤 Enviando filtros para backend (Usuário):', params);
    const response = await api.get('/relatorios/usuario', { params });
    return response.data;
  } catch (error: any) {
    console.error('Erro ao obter relatório do usuário:', error);
    throw new Error(error.response?.data?.message || 'Erro ao carregar relatório do usuário');
  }
};

// ============================================
// FUNÇÕES AUXILIARES
// ============================================

/**
 * Formata a data para o formato YYYY-MM-DD
 */
export const formatarDataParaAPI = (data: Date): string => {
  return data.toISOString().split('T')[0];
};

/**
 * Obtém o período padrão (último mês)
 */
export const obterPeriodoPadrao = (): { dataInicio: string; dataFim: string } => {
  const dataFim = new Date();
  const dataInicio = new Date();
  dataInicio.setMonth(dataInicio.getMonth() - 1);

  return {
    dataInicio: formatarDataParaAPI(dataInicio),
    dataFim: formatarDataParaAPI(dataFim)
  };
};

/**
 * Obtém o período da última semana
 */
export const obterPeriodoUltimaSemana = (): { dataInicio: string; dataFim: string } => {
  const dataFim = new Date();
  const dataInicio = new Date();
  dataInicio.setDate(dataInicio.getDate() - 7);

  return {
    dataInicio: formatarDataParaAPI(dataInicio),
    dataFim: formatarDataParaAPI(dataFim)
  };
};

/**
 * Obtém o período do mês atual
 */
export const obterPeriodoMesAtual = (): { dataInicio: string; dataFim: string } => {
  const dataFim = new Date();
  const dataInicio = new Date(dataFim.getFullYear(), dataFim.getMonth(), 1);

  return {
    dataInicio: formatarDataParaAPI(dataInicio),
    dataFim: formatarDataParaAPI(dataFim)
  };
};

/**
 * Obtém o período do trimestre atual
 */
export const obterPeriodoTrimestreAtual = (): { dataInicio: string; dataFim: string } => {
  const dataFim = new Date();
  const mesAtual = dataFim.getMonth();
  const trimestreInicio = Math.floor(mesAtual / 3) * 3;
  const dataInicio = new Date(dataFim.getFullYear(), trimestreInicio, 1);

  return {
    dataInicio: formatarDataParaAPI(dataInicio),
    dataFim: formatarDataParaAPI(dataFim)
  };
};

/**
 * Calcula a cor baseada na taxa de conclusão
 */
export const obterCorTaxaConclusao = (taxa: number): string => {
  if (taxa >= 80) return '#4caf50'; // Verde
  if (taxa >= 60) return '#ff9800'; // Laranja
  return '#f44336'; // Vermelho
};

/**
 * Formata número como porcentagem
 */
export const formatarPorcentagem = (valor: number): string => {
  return `${Math.round(valor)}%`;
};

/**
 * Formata tempo médio de conclusão
 */
export const formatarTempoConclusao = (dias: string | number): string => {
  const diasNum = typeof dias === 'string' ? parseFloat(dias) : dias;
  
  if (diasNum === 0) return 'N/A';
  if (diasNum < 1) return `${Math.round(diasNum * 24)}h`;
  if (diasNum === 1) return '1 dia';
  return `${Math.round(diasNum)} dias`;
};

/**
 * Obter métricas detalhadas de um colaborador específico
 */
export const obterMetricasColaborador = async (
  usuarioId: string,
  filtros?: FiltrosRelatorio
): Promise<MetricasColaboradorResponse> => {
  try {
    const params: any = {};
    if (filtros?.dataInicio) params.dataInicio = filtros.dataInicio;
    if (filtros?.dataFim) params.dataFim = filtros.dataFim;

    const response = await api.get(`/relatorios/colaborador/${usuarioId}`, { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Erro ao carregar métricas do colaborador');
  }
};

/**
 * Obter métricas detalhadas de um departamento específico
 */
export interface MetricasDepartamentoResponse {
  success: boolean;
  periodo: {
    dataInicio: string | null;
    dataFim: string | null;
  };
  departamento: {
    id: string;
    nome: string;
    descricao: string;
  };
  metricas: {
    totalTarefas: number;
    tarefasConcluidas: number;
    tarefasConcluidasNoPrazo: number;
    tarefasConcluidasComAtraso: number;
    tarefasPendentes: number;
    tarefasAtrasadas: number;
    taxaConclusao: number;
    tempoMedioConclusao: string;
  };
  colaboradores?: Array<{
    id: string;
    nome: string;
    cargo: string;
    avatar?: string;
    metricas: {
      totalTarefas: number;
      tarefasConcluidas: number;
      tarefasPendentes: number;
      tarefasAtrasadas: number;
      taxaConclusao: number;
    };
  }>;
}

export const obterMetricasDepartamento = async (
  departamentoId: string,
  filtros?: FiltrosRelatorio
): Promise<MetricasDepartamentoResponse> => {
  try {
    const params: Record<string, string> = {};
    if (filtros?.dataInicio) params.dataInicio = filtros.dataInicio;
    if (filtros?.dataFim) params.dataFim = filtros.dataFim;
    if (filtros?.status && filtros.status.length > 0) params.status = JSON.stringify(filtros.status);
    const response = await api.get(`/relatorios/departamento/${departamentoId}`, { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Erro ao carregar métricas do departamento');
  }
};

const relatorioService = {
  obterRelatorioLider,
  obterRelatorioAdmin,
  obterRelatorioUsuario,
  obterMetricasColaborador,
  formatarDataParaAPI,
  obterPeriodoPadrao,
  obterPeriodoUltimaSemana,
  obterPeriodoMesAtual,
  obterPeriodoTrimestreAtual,
  obterCorTaxaConclusao,
  formatarPorcentagem,
  formatarTempoConclusao
};

export default relatorioService;
